import React, { useEffect } from "react";
import {
  Box,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import "./questionPoolTable.scss";
import dayjs from "dayjs";
import { calculateAssessmentCounts } from "../../utils/utils";

const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");

dayjs.extend(utc);
dayjs.extend(timezone);

// Helper function to get topic data
const getTopicData = (topic, assessmentDetails) => {
  const topicName = topic?.name || topic?.topic?.name;
  const topicId = topic?.topicId || topic?.topic?.id;
  const topicQuestion = assessmentDetails?.topicQuestions?.find(
    (t) => (t?.topicId || t?.topic?.id) === topicId
  );
  return {
    topicName,
    topicId,
    easyQuestionsCount: topicQuestion?.easyQuestionsCount,
    mediumQuestionsCount: topicQuestion?.mediumQuestionsCount,
    hardQuestionsCount: topicQuestion?.hardQuestionsCount,
  };
};

// Helper function to get assessment counts for a difficulty level
const getAssessmentCount = (
  difficulty,
  assessmentDetails,
  assessmentQuestions
) => {
  const counts = calculateAssessmentCounts(
    assessmentDetails?.type?.key,
    assessmentQuestions
  );
  return counts?.[difficulty];
};

// Component for Question Count TextField
const QuestionCountTextField = ({
  difficulty,
  value,
  topicId,
  assessmentQuestions,
  assessmentDetails,
  topicErrors,
  apiError,
  viewAssessment,
  isEvaluationActive,
  handleAssessmentDetailsChange,
}) => {
  const difficultyKey = `${difficulty}QuestionsCount`;
  const difficultyUpper = difficulty.toUpperCase();
  const assessmentCount = getAssessmentCount(
    difficultyUpper,
    assessmentDetails,
    assessmentQuestions
  );
  const exceedsMax = viewAssessment && assessmentCount < value;
  const hasError = apiError || topicErrors?.[difficultyKey] || exceedsMax;
  const helperText =
    topicErrors?.[difficultyKey] || (exceedsMax && "Exceeds maximum count");

  return (
    <TextField
      disabled={viewAssessment || isEvaluationActive}
      size="small"
      color="secondary"
      sx={{ maxWidth: "12.25rem" }}
      className="filter-inputs"
      margin="dense"
      variant="outlined"
      value={value}
      onChange={(e) =>
        handleAssessmentDetailsChange(
          difficultyKey,
          +e?.target?.value,
          topicId,
          assessmentQuestions
        )
      }
      error={hasError}
      helperText={helperText}
      InputProps={{
        endAdornment: (
          <InputAdornment
            position="end"
            sx={{
              "& .MuiTypography-root": { color: "#948B9E" },
            }}
          >
            {`/${assessmentCount}`}
          </InputAdornment>
        ),
      }}
    />
  );
};

// Component for Topic Table Row
const TopicTableRow = ({
  topic,
  index,
  assessmentDetails,
  errors,
  apiError,
  viewAssessment,
  isEvaluationActive,
  handleAssessmentDetailsChange,
}) => {
  const {
    topicName,
    topicId,
    easyQuestionsCount,
    mediumQuestionsCount,
    hardQuestionsCount,
  } = getTopicData(topic, assessmentDetails);
  const assessmentQuestions = viewAssessment
    ? topic?.topic?.activeAssessmentQuestionCount
    : topic?.activeAssessmentQuestionCount;
  const topicErrors = errors?.topicQuestions?.[topicId] || {};

  return (
    <TableRow className="table-body-row" key={index}>
      <TableCell className="padding-left-1">
        <Typography variant="font_12">{topicName}</Typography>
        {!viewAssessment && !!errors.topicQuestions && topicErrors?.noData && (
          <>
            <br />
            <Typography
              color={topicErrors?.noData || apiError ? "error" : "offline"}
              variant="font_12"
              sx={{ marginBottom: "1rem" }}
            >
              {topicErrors?.noData}
            </Typography>
          </>
        )}
      </TableCell>
      <TableCell align="left" className="padding-left-1">
        <QuestionCountTextField
          difficulty="easy"
          value={easyQuestionsCount}
          topicId={topicId}
          assessmentQuestions={assessmentQuestions}
          assessmentDetails={assessmentDetails}
          topicErrors={topicErrors}
          apiError={apiError}
          viewAssessment={viewAssessment}
          isEvaluationActive={isEvaluationActive}
          handleAssessmentDetailsChange={handleAssessmentDetailsChange}
        />
      </TableCell>
      <TableCell className="padding-left-1">
        <QuestionCountTextField
          difficulty="medium"
          value={mediumQuestionsCount}
          topicId={topicId}
          assessmentQuestions={assessmentQuestions}
          assessmentDetails={assessmentDetails}
          topicErrors={topicErrors}
          apiError={apiError}
          viewAssessment={viewAssessment}
          isEvaluationActive={isEvaluationActive}
          handleAssessmentDetailsChange={handleAssessmentDetailsChange}
        />
      </TableCell>
      <TableCell className="padding-left-1">
        <QuestionCountTextField
          difficulty="hard"
          value={hardQuestionsCount}
          topicId={topicId}
          assessmentQuestions={assessmentQuestions}
          assessmentDetails={assessmentDetails}
          topicErrors={topicErrors}
          apiError={apiError}
          viewAssessment={viewAssessment}
          isEvaluationActive={isEvaluationActive}
          handleAssessmentDetailsChange={handleAssessmentDetailsChange}
        />
      </TableCell>
    </TableRow>
  );
};

const QuestionPoolTable = ({
  handleAssessmentDetailsChange,
  selectedSessions,
  assessmentTopicQuestions,
  errors,
  apiError = false,
  assessmentDetails,
  setAssessmentDetails,
  setInitialFormDetails,
  viewAssessment,
  isEvaluationActive,
}) => {
  const topicList = selectedSessions
    ?.map((session) => session?.topic)
    ?.filter(
      (topic, index, self) =>
        topic && index === self.findIndex((t) => t?.id === topic.id) // Filter unique topics by id
    );
  const topicListSelectData = topicList?.map((item) => ({
    topicId: item?.id,
    name: item?.name,
    easyQuestionsCount:
      assessmentDetails?.topicQuestions?.find(
        (topic) => topic.topicId === item.id
      )?.easyQuestionsCount || 0,
    mediumQuestionsCount:
      assessmentDetails?.topicQuestions?.find(
        (topic) => topic.topicId === item.id
      )?.mediumQuestionsCount || 0,
    hardQuestionsCount:
      assessmentDetails?.topicQuestions?.find(
        (topic) => topic.topicId === item.id
      )?.hardQuestionsCount || 0,
    activeAssessmentQuestionCount: item?.activeAssessmentQuestionCount,
  }));

  useEffect(() => {
    if (topicListSelectData && selectedSessions?.length) {
      setAssessmentDetails((prev) => ({
        ...prev,
        topicQuestions: topicListSelectData,
      }));
      setInitialFormDetails((prev) => ({
        ...prev,
        topicQuestions: topicListSelectData,
      }));
    }
  }, [selectedSessions]);

  return (
    <>
      <TableContainer className="table-container question-pool-table-container">
        <Table
          stickyHeader
          aria-label="sticky table"
          style={{ minWidth: "100%", marginBottom: "0.75rem" }}
        >
          <TableHead className="table-head-container">
            <TableRow className="table-head-row">
              <TableCell align="left">
                <Typography
                  variant="font_13_bold"
                  className="flex-table-sort-head"
                  sx={{ minWidth: "20rem" }}
                >
                  <Box className="table-sort-head">Topic</Box>
                </Typography>
              </TableCell>
              <TableCell
                align="left"
                sx={{ minWidth: "10rem" }}
                className="padding-left-1"
              >
                <Typography variant="font_13_bold">Easy</Typography>
              </TableCell>
              <TableCell
                align="left"
                sx={{ minWidth: "10rem" }}
                className="padding-left-1"
              >
                <Typography sx={{ minWidth: "10rem" }} variant="font_13_bold">
                  Medium
                </Typography>
              </TableCell>
              <TableCell
                align="left"
                sx={{ minWidth: "10rem" }}
                className="padding-left-1"
              >
                <Typography
                  variant="font_13_bold"
                  className="flex-table-sort-head"
                  sx={{ minWidth: "10rem" }}
                >
                  Hard
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(viewAssessment
              ? assessmentTopicQuestions
              : topicListSelectData
            )?.map((topic, index) => (
              <TopicTableRow
                key={index}
                topic={topic}
                index={index}
                assessmentDetails={assessmentDetails}
                errors={errors}
                apiError={apiError}
                viewAssessment={viewAssessment}
                isEvaluationActive={isEvaluationActive}
                handleAssessmentDetailsChange={handleAssessmentDetailsChange}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default QuestionPoolTable;
